import type { AppFeatures, NonUserAppFeatures } from "../components/SerializedApp";
import { AppKind } from "../components/SerializedApp";
import { assertNever } from "../Support";

export enum Eminence {
    Free = "free",
    OrgFree = "org-free",
    OrgFreePage = "org-free-page",
    Pro = "pro",
    OrgUnpublished = "org-unpublished",
    OrgInternal = "org-internal",
    OrgPublic = "org-public",
    UnifiedBasic = "unified-basic",
    UnifiedPro = "unified-pro",
    UnifiedProPrivate = "unified-private",
    UnifiedProPrivateTrial = "unified-private-trial",
    UnifiedBasicPrivate = "unified-basic-private",
    OrgUnifiedProPrivate = "org-unified-pro-private",
    OrgUnifiedProPrivatePage = "org-unified-pro-private-page",
    OrgUnifiedBasicPrivate = "org-unified-basic-private",
}

interface NonUserAppFeaturesOverride {
    readonly noRowLimit: boolean;
    readonly noGeocodeLimit: boolean;
    readonly canHaveEmailWhitelist: boolean;
}

export function isFreeEminence(e: Eminence): e is Eminence.Free | Eminence.OrgFree | Eminence.OrgFreePage {
    return e === Eminence.Free || e === Eminence.OrgFree || e === Eminence.OrgFreePage;
}

// `true` means supported.
// `"upgrade"` means not supported, but upgrading adds support.
// `false` means not supported, and no way to get support.
export type SupportLevel = boolean | "upgrade";

export interface AuthenticationSupport {
    readonly public: SupportLevel;
    readonly publicWithEmail: SupportLevel;
    readonly publicDisabled: SupportLevel;
    readonly password: SupportLevel;
    readonly emailWhitelist: SupportLevel;
    readonly organizationMembers: SupportLevel;
    readonly justMe: SupportLevel;
    readonly userProfiles: SupportLevel;

    // Can Public with Email be made optional?
    readonly optionalPublicWithEmail: SupportLevel;
    // Can the other Email Pin auth methods be made optional?
    readonly optionalOtherEmail: SupportLevel;
}

export type TransferToOrgAs = "internal" | "public" | "free" | undefined;

export interface EminenceFlagsBase {
    readonly base64EncodePublicUserProfiles: boolean;
    readonly needsBuilderModifyAppFeatures: boolean;
    readonly nonUserAppFeaturesOverride: NonUserAppFeaturesOverride;
    readonly removeBranding: boolean;
    readonly inYourFaceBranding: boolean;
    readonly lowTransactionFees: boolean;
    readonly googleSharedDrive: boolean;
    readonly customDomains: boolean;
    readonly badge: string | undefined;
    readonly withGrandfathering: boolean;
    readonly automaticRefresh: boolean;
    readonly googleAnalytics: boolean;
    readonly unlimitedZapier: boolean;
    readonly zapQuota: number;
    readonly signatureQuota: number;
    readonly customSignIn: boolean;
    readonly authenticationSupport: AuthenticationSupport;
    readonly upgradeButton: boolean;
    readonly proComponents: boolean;
    readonly proIcons: boolean;
    readonly isUserMetered: boolean;
    readonly canDisableShare: boolean;
    // Org apps don't support the Buy Button yet
    readonly buyButton: boolean;
    readonly canUpgrade: boolean;
    readonly canDowngrade: boolean;
    // Can this app be transferred?  NOTE: If we want to
    // allow this for non-free apps, we will also have to implement
    // all the Pro bookkeeping.
    readonly canTransferBetweenUsers: boolean;
    readonly canTransferToOrg: boolean;
    readonly canUnpublish: boolean;
    readonly canPausePublish: boolean;
    readonly canUseSignInWithGoogle: boolean;
    // Everything can be deleted, but we sometimes need to downgrade
    // apps before deletion.
    readonly downgradeBeforeDelete: boolean;
    readonly downgradeBeforeDeleteStripeCancellation: "immediately" | undefined;
    readonly logOrgLogin: boolean;
    readonly allowUsersToCopyApp: boolean;
    readonly canSubmitTemplate: boolean;
    readonly needsPublishableOrgToTransfer: boolean;
    readonly transferToOrgAs: TransferToOrgAs;
    readonly showQuotaBanners: boolean;
    readonly unlimitedPasswordAndTeamMembersOnlyUsers: boolean;
    readonly reloadQuota: number;
    readonly rowQuota: number;
    readonly proDisplayQuotas: boolean;
    readonly canHaveBoosts: boolean;
    readonly tabletMode: boolean;
    readonly showReportApp: boolean;
    readonly confirmChangeAuthOnUpgrade: boolean;
    readonly roles: boolean;
    readonly protectedColumns: boolean;
    readonly allowRealEmails: boolean;
    readonly allowVirtualEmails: boolean;
    readonly stripeTransactionFeePercentage: number;
    readonly deliverEmailQuota: number;
    readonly fileStorageQuota: number;
    readonly fileStorageQuotaIsAdvisory: boolean;
    readonly canChangeAuthType: boolean;
    readonly isPrivateProTrial: boolean;
    readonly barcodeScanQuota: number;
}

export interface EminenceFlags extends EminenceFlagsBase {
    readonly eminence: Eminence;
    readonly displayName: string;
}

const freeNonUserAppFeatures: NonUserAppFeaturesOverride = {
    noRowLimit: false,
    noGeocodeLimit: false,
    canHaveEmailWhitelist: false,
};

const proNonUserAppFeatures: NonUserAppFeaturesOverride = {
    noRowLimit: true,
    noGeocodeLimit: true,
    canHaveEmailWhitelist: true,
};

const unifiedPublicFeatures: NonUserAppFeaturesOverride = {
    noRowLimit: false,
    noGeocodeLimit: true,
    canHaveEmailWhitelist: false,
};

const unifiedPrivateFeatures: NonUserAppFeaturesOverride = {
    noRowLimit: false,
    noGeocodeLimit: true,
    canHaveEmailWhitelist: true,
};

export const maxQuotaNumber = 9999999;

const proEminenceFlags = {
    confirmChangeAuthOnUpgrade: true,
    needsBuilderModifyAppFeatures: false,
    nonUserAppFeaturesOverride: proNonUserAppFeatures,
    removeBranding: true,
    inYourFaceBranding: false,
    lowTransactionFees: true,
    googleSharedDrive: true,
    customDomains: true,
    withGrandfathering: false,
    automaticRefresh: true,
    googleAnalytics: true,
    isUserMetered: false,
    unlimitedZapier: true,
    zapQuota: maxQuotaNumber,
    signatureQuota: maxQuotaNumber,
    customSignIn: true,
    proComponents: true,
    proIcons: true,
    canDisableShare: true,
    canTransferBetweenUsers: false,
    canUnpublish: false,
    canPausePublish: true,
    canUseSignInWithGoogle: false,
    downgradeBeforeDeleteStripeCancellation: undefined,
    buyButton: true,
    showQuotaBanners: false,
    unlimitedPasswordAndTeamMembersOnlyUsers: true,
    reloadQuota: maxQuotaNumber,
    rowQuota: maxQuotaNumber,
    proDisplayQuotas: true,
    tabletMode: true,
    showReportApp: false,
    protectedColumns: true,
    allowRealEmails: true,
    allowVirtualEmails: true,
    stripeTransactionFeePercentage: 2,
    fileStorageQuota: 10 * 1000 * 1000 * 1000,
    base64EncodePublicUserProfiles: true,
    canChangeAuthType: true,
    isPrivateProTrial: false,
    barcodeScanQuota: 1000,
    fileStorageQuotaIsAdvisory: true,
};

const unifiedBasicEminenceFlags: EminenceFlagsBase = {
    needsBuilderModifyAppFeatures: false,
    nonUserAppFeaturesOverride: unifiedPublicFeatures,
    removeBranding: false,
    inYourFaceBranding: false,
    lowTransactionFees: false,
    isUserMetered: false,
    googleSharedDrive: false,
    confirmChangeAuthOnUpgrade: true,
    customDomains: false,
    badge: "Basic",
    withGrandfathering: false,
    automaticRefresh: false,
    googleAnalytics: false,
    unlimitedZapier: false,
    zapQuota: 500,
    signatureQuota: 50,
    customSignIn: false,
    authenticationSupport: {
        public: true,
        publicWithEmail: true,
        publicDisabled: false,
        password: false,
        emailWhitelist: false,
        userProfiles: false,
        organizationMembers: false,
        justMe: false,
        optionalPublicWithEmail: true,
        optionalOtherEmail: false,
    },
    upgradeButton: true,
    proComponents: false,
    proIcons: false,
    canDisableShare: false,
    buyButton: true,
    canUpgrade: false,
    canDowngrade: false,
    canTransferBetweenUsers: false,
    canTransferToOrg: true,
    canUnpublish: false,
    canPausePublish: false,
    canUseSignInWithGoogle: true,
    downgradeBeforeDelete: true,
    downgradeBeforeDeleteStripeCancellation: undefined,
    logOrgLogin: false,
    allowUsersToCopyApp: false,
    canSubmitTemplate: true,
    needsPublishableOrgToTransfer: false,
    transferToOrgAs: "public",
    showQuotaBanners: true,
    unlimitedPasswordAndTeamMembersOnlyUsers: false,
    reloadQuota: 5000,
    rowQuota: 5000,
    proDisplayQuotas: false,
    canHaveBoosts: true,
    tabletMode: false,
    showReportApp: true,
    roles: false,
    protectedColumns: false,
    allowRealEmails: false,
    allowVirtualEmails: true,
    stripeTransactionFeePercentage: 5,
    deliverEmailQuota: 0,
    fileStorageQuota: 2 * 1000 * 1000 * 1000,
    base64EncodePublicUserProfiles: true,
    canChangeAuthType: true,
    isPrivateProTrial: false,
    barcodeScanQuota: 100,
    fileStorageQuotaIsAdvisory: false,
};

const unifiedBasicPrivateEminenceFlags: EminenceFlagsBase = {
    ...unifiedBasicEminenceFlags,
    nonUserAppFeaturesOverride: unifiedPrivateFeatures,
    isUserMetered: true,
    confirmChangeAuthOnUpgrade: false,
    authenticationSupport: {
        public: false,
        publicWithEmail: false,
        publicDisabled: false,
        password: true,
        emailWhitelist: true,
        userProfiles: true,
        organizationMembers: false,
        justMe: true,
        optionalPublicWithEmail: true,
        optionalOtherEmail: false,
    },
    unlimitedPasswordAndTeamMembersOnlyUsers: true,
    transferToOrgAs: "internal",
    roles: true,
    base64EncodePublicUserProfiles: false,
};

const unifiedProEminenceFlags: EminenceFlagsBase = {
    ...unifiedBasicEminenceFlags,
    removeBranding: true,
    lowTransactionFees: true,
    automaticRefresh: true,
    customSignIn: true,
    customDomains: true,
    badge: "Pro",
    googleAnalytics: true,
    unlimitedZapier: true,
    showQuotaBanners: false,
    zapQuota: maxQuotaNumber,
    signatureQuota: maxQuotaNumber,
    reloadQuota: maxQuotaNumber,
    rowQuota: maxQuotaNumber,
    tabletMode: true,
    proDisplayQuotas: true,
    showReportApp: false,
    stripeTransactionFeePercentage: 2,
    upgradeButton: false,
    allowRealEmails: true,
    allowVirtualEmails: true,
    fileStorageQuota: 10 * 1000 * 1000 * 1000,
    base64EncodePublicUserProfiles: true,
    fileStorageQuotaIsAdvisory: true,
    canPausePublish: true,
    googleSharedDrive: true,
    proComponents: true,
    proIcons: true,
    canDisableShare: true,
    protectedColumns: true,
    barcodeScanQuota: 1000,
};

const unifiedPrivateEminenceFlags: EminenceFlagsBase = {
    ...unifiedProEminenceFlags,
    nonUserAppFeaturesOverride: unifiedPrivateFeatures,
    isUserMetered: true,
    confirmChangeAuthOnUpgrade: false,
    badge: "Pro",
    authenticationSupport: {
        public: false,
        publicWithEmail: false,
        publicDisabled: false,
        password: true,
        emailWhitelist: true,
        userProfiles: true,
        organizationMembers: false,
        justMe: true,
        optionalPublicWithEmail: true,
        optionalOtherEmail: false,
    },
    unlimitedPasswordAndTeamMembersOnlyUsers: true,
    transferToOrgAs: "internal",
    roles: true,
    base64EncodePublicUserProfiles: false,
};

const freeFlags: EminenceFlags = {
    eminence: Eminence.Free,
    displayName: "Personal",
    needsBuilderModifyAppFeatures: true,
    nonUserAppFeaturesOverride: freeNonUserAppFeatures,
    removeBranding: false,
    inYourFaceBranding: true,
    lowTransactionFees: false,
    googleSharedDrive: false,
    confirmChangeAuthOnUpgrade: true,
    customDomains: false,
    badge: undefined,
    isUserMetered: false,
    withGrandfathering: true,
    automaticRefresh: false,
    googleAnalytics: false,
    unlimitedZapier: false,
    zapQuota: 100,
    signatureQuota: 10,
    customSignIn: false,
    authenticationSupport: {
        public: true,
        publicWithEmail: true,
        publicDisabled: false,
        password: true,
        emailWhitelist: "upgrade",
        userProfiles: "upgrade",
        organizationMembers: false,
        justMe: true,
        optionalPublicWithEmail: true,
        optionalOtherEmail: false,
    },
    upgradeButton: true,
    proComponents: false,
    proIcons: false,
    canDisableShare: false,
    buyButton: true,
    canUpgrade: true,
    canDowngrade: false,
    canTransferBetweenUsers: true,
    canTransferToOrg: true,
    canUnpublish: false,
    canPausePublish: false,
    canUseSignInWithGoogle: true,
    downgradeBeforeDelete: false,
    downgradeBeforeDeleteStripeCancellation: undefined,
    logOrgLogin: false,
    allowUsersToCopyApp: true,
    canSubmitTemplate: true,
    needsPublishableOrgToTransfer: false,
    transferToOrgAs: "free",
    showQuotaBanners: true,
    unlimitedPasswordAndTeamMembersOnlyUsers: false,
    reloadQuota: 1000,
    rowQuota: 500,
    proDisplayQuotas: false,
    canHaveBoosts: true,
    tabletMode: false,
    showReportApp: true,
    roles: false,
    protectedColumns: false,
    allowRealEmails: false,
    allowVirtualEmails: true,
    stripeTransactionFeePercentage: 10,
    deliverEmailQuota: 0,
    fileStorageQuota: 100 * 1000 * 1000,
    base64EncodePublicUserProfiles: true,
    canChangeAuthType: true,
    isPrivateProTrial: false,
    barcodeScanQuota: 10,
    fileStorageQuotaIsAdvisory: false,
};

const orgFreeFlags = {
    ...freeFlags,
    eminence: Eminence.OrgFree,
    authenticationSupport: {
        ...freeFlags.authenticationSupport,
        organizationMembers: true,
    },
};

const orgUnifiedProPrivateFlags = {
    eminence: Eminence.OrgUnifiedProPrivate,
    displayName: "Pro Private",
    ...unifiedPrivateEminenceFlags,
    authenticationSupport: {
        public: false,
        publicWithEmail: false,
        publicDisabled: false,
        password: true,
        emailWhitelist: true,
        userProfiles: true,
        organizationMembers: true,
        justMe: false,
        optionalPublicWithEmail: false,
        optionalOtherEmail: false,
    },
    deliverEmailQuota: 1000,
};

export const eminenceFlags: Record<Eminence, EminenceFlags> = {
    [Eminence.Free]: freeFlags,
    [Eminence.OrgFree]: orgFreeFlags,
    [Eminence.OrgFreePage]: {
        ...orgFreeFlags,
        eminence: Eminence.OrgFreePage,
        rowQuota: 100,
        unlimitedPasswordAndTeamMembersOnlyUsers: true,
        allowRealEmails: true,
        allowVirtualEmails: false,
        authenticationSupport: {
            public: false,
            publicWithEmail: "upgrade",
            publicDisabled: true,
            password: false,
            emailWhitelist: false,
            userProfiles: "upgrade",
            organizationMembers: true,
            justMe: false,
            optionalPublicWithEmail: "upgrade",
            optionalOtherEmail: "upgrade",
        },
    },
    [Eminence.Pro]: {
        eminence: Eminence.Pro,
        displayName: "Pro",
        ...proEminenceFlags,
        badge: "Pro",
        authenticationSupport: {
            public: true,
            publicWithEmail: true,
            publicDisabled: false,
            password: true,
            emailWhitelist: true,
            userProfiles: true,
            organizationMembers: false,
            justMe: true,
            optionalPublicWithEmail: true,
            optionalOtherEmail: false,
        },
        upgradeButton: false,
        canUpgrade: false,
        canDowngrade: true,
        canTransferToOrg: true,
        canUseSignInWithGoogle: false,
        downgradeBeforeDelete: true,
        downgradeBeforeDeleteStripeCancellation: "immediately",
        logOrgLogin: false,
        allowUsersToCopyApp: true,
        canSubmitTemplate: true,
        needsPublishableOrgToTransfer: true,
        transferToOrgAs: "public",
        canHaveBoosts: false,
        roles: true,
        deliverEmailQuota: 0,
    },
    [Eminence.OrgUnpublished]: {
        eminence: Eminence.OrgUnpublished,
        displayName: "Organization",
        ...proEminenceFlags,
        badge: undefined,
        authenticationSupport: {
            public: true,
            publicWithEmail: true,
            publicDisabled: false,
            password: true,
            emailWhitelist: true,
            userProfiles: true,
            organizationMembers: true,
            justMe: false,
            optionalPublicWithEmail: true,
            optionalOtherEmail: false,
        },
        upgradeButton: false,
        canUpgrade: true,
        canDowngrade: false,
        canTransferToOrg: false,
        downgradeBeforeDelete: false,
        logOrgLogin: false,
        allowUsersToCopyApp: false,
        canSubmitTemplate: false,
        needsPublishableOrgToTransfer: false,
        transferToOrgAs: undefined,
        canHaveBoosts: false,
        roles: true,
        deliverEmailQuota: 0,
        fileStorageQuotaIsAdvisory: false,
    },
    [Eminence.OrgInternal]: {
        eminence: Eminence.OrgInternal,
        displayName: "Organization",
        ...proEminenceFlags,
        badge: undefined,
        authenticationSupport: {
            public: false,
            publicWithEmail: false,
            publicDisabled: false,
            password: false,
            emailWhitelist: true,
            userProfiles: true,
            organizationMembers: true,
            justMe: false,
            optionalPublicWithEmail: false,
            optionalOtherEmail: false,
        },
        upgradeButton: false,
        canUpgrade: false,
        canDowngrade: false,
        canTransferToOrg: false,
        downgradeBeforeDelete: true,
        logOrgLogin: true,
        allowUsersToCopyApp: false,
        canSubmitTemplate: false,
        needsPublishableOrgToTransfer: false,
        transferToOrgAs: undefined,
        canHaveBoosts: false,
        roles: true,
        deliverEmailQuota: 0,
        fileStorageQuotaIsAdvisory: false,
    },
    [Eminence.OrgPublic]: {
        eminence: Eminence.OrgPublic,
        displayName: "Organization",
        ...proEminenceFlags,
        badge: "Pro",
        authenticationSupport: {
            public: true,
            publicWithEmail: true,
            publicDisabled: false,
            password: true,
            emailWhitelist: true,
            userProfiles: true,
            organizationMembers: false,
            justMe: false,
            optionalPublicWithEmail: true,
            optionalOtherEmail: false,
        },
        upgradeButton: false,
        canUpgrade: false,
        canDowngrade: false,
        canTransferToOrg: false,
        canUnpublish: true,
        downgradeBeforeDelete: true,
        logOrgLogin: false,
        allowUsersToCopyApp: false,
        canSubmitTemplate: false,
        needsPublishableOrgToTransfer: false,
        transferToOrgAs: undefined,
        canHaveBoosts: false,
        roles: false,
        deliverEmailQuota: 0,
    },
    [Eminence.UnifiedBasic]: {
        eminence: Eminence.UnifiedBasic,
        displayName: "Basic",
        ...unifiedBasicEminenceFlags,
    },
    [Eminence.UnifiedPro]: {
        eminence: Eminence.UnifiedPro,
        displayName: "Pro",
        ...unifiedProEminenceFlags,
    },
    [Eminence.UnifiedProPrivate]: {
        eminence: Eminence.UnifiedProPrivate,
        displayName: "Pro Private",
        ...unifiedPrivateEminenceFlags,
        deliverEmailQuota: 1000,
    },
    [Eminence.UnifiedProPrivateTrial]: {
        eminence: Eminence.UnifiedProPrivateTrial,
        displayName: "Private Pro Trial",
        ...unifiedPrivateEminenceFlags,
        badge: "Private Pro Trial",
        deliverEmailQuota: 1000,
        canChangeAuthType: false,
        downgradeBeforeDelete: false,
        canUpgrade: true,
        upgradeButton: true,
        isPrivateProTrial: true, // FIXME: Should be 2 eminences rather than this stupid check. We should have a private pro trial eminence as well a expired trial eminence and the flags should provide expected behavior in each case
    },
    [Eminence.UnifiedBasicPrivate]: {
        eminence: Eminence.UnifiedBasicPrivate,
        displayName: "Basic Private",
        ...unifiedBasicPrivateEminenceFlags,
    },
    [Eminence.OrgUnifiedProPrivate]: orgUnifiedProPrivateFlags,
    [Eminence.OrgUnifiedProPrivatePage]: {
        ...orgUnifiedProPrivateFlags,
        eminence: Eminence.OrgUnifiedProPrivatePage,
        allowRealEmails: true,
        allowVirtualEmails: false,
        authenticationSupport: {
            public: false,
            publicWithEmail: true,
            publicDisabled: true,
            password: false,
            emailWhitelist: false,
            userProfiles: true,
            organizationMembers: true,
            justMe: false,
            optionalPublicWithEmail: true,
            optionalOtherEmail: true,
        },
    },
    [Eminence.OrgUnifiedBasicPrivate]: {
        eminence: Eminence.OrgUnifiedBasicPrivate,
        displayName: "Basic Private",
        ...unifiedBasicPrivateEminenceFlags,
        authenticationSupport: {
            public: false,
            publicWithEmail: false,
            publicDisabled: false,
            password: true,
            emailWhitelist: true,
            userProfiles: true,
            organizationMembers: true,
            justMe: false,
            optionalPublicWithEmail: false,
            optionalOtherEmail: false,
        },
    },
};

// This enforces app features based on eminence.  There's a somewhat similar
// function `adaptAppToEminence` which is used to set features to new defaults
// when an app's eminence changes.  We're using this in
// `AppInfoFetcher.getAppFeatures`, so it's used on the backend when getting
// app features from the builder app, too.
export function enforceEminenceInAppFeatures(
    {
        removeBranding,
        withGrandfathering,
        nonUserAppFeaturesOverride,
        googleAnalytics,
        allowUsersToCopyApp,
        canDisableShare,
        automaticRefresh,
        tabletMode,
        customSignIn,
        isPrivateProTrial,
        allowRealEmails,
        allowVirtualEmails,
    }: EminenceFlags,
    features: AppFeatures,
    grandfatheredFeatures: Partial<AppFeatures>,
    daysLeftInTrial: number | undefined
): AppFeatures {
    const trialExpired = daysLeftInTrial !== undefined && daysLeftInTrial <= 0;
    features = {
        ...features,
        ...nonUserAppFeaturesOverride,
        removeBranding,
        useCustomSignInBackgroundImageInLoading: customSignIn
            ? features.useCustomSignInBackgroundImageInLoading ?? false
            : false,
        allowTabletMode: tabletMode,
        googleAnalyticsMeasureIDs: googleAnalytics ? features.googleAnalyticsMeasureIDs : [],
        enableShareScreen: canDisableShare ? features.enableShareScreen === true : true,
        disableSharing: canDisableShare ? features.disableSharing : false,
        isWorldCopyable: allowUsersToCopyApp && features.isWorldCopyable === true,
        automaticRefreshMinutes: automaticRefresh ? features.automaticRefreshMinutes : undefined,
        defaultTabletMode: tabletMode ? features.defaultTabletMode : false,
        isTrialLocked: isPrivateProTrial && trialExpired,
        virtualEmailAddresses: !allowRealEmails ? true : !allowVirtualEmails ? false : features.virtualEmailAddresses,
    };
    if (withGrandfathering) {
        features = { ...features, ...grandfatheredFeatures };
    }
    return features;
}

export function getBaseRowQuota(appFeatures: NonUserAppFeatures | undefined): number | undefined;
export function getBaseRowQuota(appFeatures: NonUserAppFeatures | undefined, flags: EminenceFlags): number;
export function getBaseRowQuota(
    appFeatures: NonUserAppFeatures | undefined,
    flags?: EminenceFlags
): number | undefined {
    if (appFeatures?.noRowLimit === true) {
        return proEminenceFlags.rowQuota;
    }
    return flags?.rowQuota;
}

export function getFreeEminence(appKind: AppKind): Eminence {
    if (appKind === AppKind.App) {
        return Eminence.OrgFree;
    } else if (appKind === AppKind.Page) {
        return Eminence.OrgFreePage;
    } else {
        return assertNever(appKind);
    }
}
